import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, TrendingUp, Clock, Eye, AlertTriangle, Camera, Copy, Send, Loader2, ExternalLink, Play, ShieldCheck } from 'lucide-react';
import TestCaseSearchBar from './components/TestCaseSearchBar';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import azureDevOpsService from './services/azureDevOpsService';
import html2canvas from 'html2canvas';
import toast from 'react-hot-toast';
import { sendDashboardToSlack } from './services/slackService';
import { getStatusFromSuccessRate } from './config/statusConfig';
import SQUAD_CONFIGS from './config/squads.json';
import { computeSuccessRate } from './utils/testStatus';
import rootCauseService from './services/rootCauseService';
import SkippedTestsPanel from './components/SkippedTestsPanel';
import { summarizeVerifications, useSuiteVerifications } from './services/suiteVerificationService';
import { useKnownFailures } from './services/knownFailuresService';

const READYAPI_PIPELINE_CONFIGS = {
  develop: {
    label: '26.1',
    pipelinesList: [
      { id: 497, name: 'DP Tests' },
      { id: 499, name: 'DE Tests' },
      { id: 496, name: 'Admin Tests' },
      { id: 501, name: 'Investigation Tests' }
    ],
    pipelineIds: [497, 499, 496, 501]
  },
  '26.2': {
    label: '26.2',
    pipelinesList: [
      { id: 497, name: 'DP Tests' },
      { id: 499, name: 'DE Tests' },
      { id: 496, name: 'Admin Tests' },
      { id: 501, name: 'Investigation Tests' }
    ],
    pipelineIds: [497, 499, 496, 501]
  },
  '25.1': {
    label: '25.1',
    pipelinesList: [
      { id: 389, name: 'DP Tests' },
      { id: 392, name: 'DE Tests' },
      { id: 390, name: 'Admin Tests' },
      { id: 391, name: 'Investigation Tests' }
    ],
    pipelineIds: [389, 392, 390, 391]
  },
  '25.2': {
    label: '25.2',
    pipelinesList: [
      { id: 518, name: 'DP Tests' },
      { id: 519, name: 'DE Tests' },
      { id: 517, name: 'Admin Tests' },
      { id: 520, name: 'Investigation Tests' }
    ],
    pipelineIds: [518, 519, 517, 520]
  }
};

const ROBOT_PIPELINE_CONFIGS = {
  'develop': {
    label: '26.1',
    pipelinesList: [
      { id: 601, name: 'RF Admin API',         domain: 'Admin',         env: 'QA1' },
      { id: 602, name: 'RF Admin Browser',     domain: 'Admin',         env: 'QA1' },
      { id: 603, name: 'RF Config API',        domain: 'Config',        env: 'QA1' },
      { id: 604, name: 'RF Config Browser',    domain: 'Config',        env: 'QA1' },
      { id: 605, name: 'RF DE API',            domain: 'DE',            env: 'QA1' },
      { id: 606, name: 'RF DE Browser',        domain: 'DE',            env: 'QA1' },
      { id: 607, name: 'RF Pelias',            domain: 'Pelias',        env: 'QA1' },
      { id: 608, name: 'RF DP API',            domain: 'DP',            env: 'QA3' },
      { id: 609, name: 'RF DP Browser',        domain: 'DP',            env: 'QA3' },
      { id: 610, name: 'RF Investigation API', domain: 'Investigation', env: 'QA3' },
      { id: 615, name: 'RF OSINT API',         domain: 'OSINT',         env: 'val9' },
      { id: 616, name: 'RF OSINT Browser',     domain: 'OSINT',         env: 'val9' },
    ],
    pipelineIds: [601, 602, 603, 604, 605, 606, 607, 608, 609, 610, 615, 616]
  },
  '26.2': {
    label: '26.2',
    pipelinesList: [
      { id: 611, name: 'RF Agentique API',      domain: 'Agentique',     env: 'val05' },
      { id: 612, name: 'RF Agentique Browser',  domain: 'Agentique',     env: 'val05' },
      { id: 613, name: 'RF Assistant API',      domain: 'Assistant',     env: 'val05' },
      { id: 614, name: 'RF Assistant Browser',  domain: 'Assistant',     env: 'val05' },
      { id: 632, name: 'RF Workplace API',      domain: 'Workplace',     env: 'val05' },
      { id: 685, name: 'RF Workplace Browser',  domain: 'Workplace',     env: 'val05' },
      { id: 633, name: 'RF Admin API',          domain: 'Admin',         env: 'QA1'   },
      { id: 634, name: 'RF Admin Browser',      domain: 'Admin',         env: 'QA1'   },
      { id: 635, name: 'RF Config API',         domain: 'Config',        env: 'QA1'   },
      { id: 636, name: 'RF Config Browser',     domain: 'Config',        env: 'QA1'   },
      { id: 637, name: 'RF DE API',             domain: 'DE',            env: 'QA1'   },
      { id: 638, name: 'RF DE Browser',         domain: 'DE',            env: 'QA1'   },
      { id: 639, name: 'RF Pelias',             domain: 'Pelias',        env: 'QA1'   },
      { id: 640, name: 'RF DP API',             domain: 'DP',            env: 'QA3'   },
      { id: 641, name: 'RF DP Browser',         domain: 'DP',            env: 'QA3'   },
      { id: 642, name: 'RF Investigation API',  domain: 'Investigation', env: 'QA3'   },
      { id: 643, name: 'RF OSINT API',          domain: 'OSINT',         env: 'val9'  },
      { id: 644, name: 'RF OSINT Browser',      domain: 'OSINT',         env: 'val9'  },
    ],
    pipelineIds: [611, 612, 613, 614, 632, 685, 633, 634, 635, 636, 637, 638, 639, 640, 641, 642, 643, 644]
  },
  '25.2': {
    label: '25.2',
    pipelinesList: [
      { id: 526, name: 'RobotFramework Browser' },
      { id: 525, name: 'RobotFramework API' }
    ],
    pipelineIds: [526, 525]
  }
};

// Mapping squad → pipelines (src/config/squads.json), partagé avec la route serveur /api/squads/status.
// rfConfigs doit correspondre exactement aux testSuiteNames publiés dans AzDO ;
// readyApiPipelineId : pipeline ReadyAPI inclus dans les stats globales de la page d'équipe (null si absent) ;
// deployPipeline : pipeline de déploiement Proxmox (Backbone) — la suite ne tourne qu'en fin de
// déploiement et un déploiement cassé avant ne publie rien, on prend donc le dernier build qui a
// réellement joué la suite, et l'environnement vient du pipeline lui-même.
const SQUAD_CONFIGS_DEVELOP = SQUAD_CONFIGS.develop;

const ROUTE_VERSION_KEYS = {
  '/framework/back': 'frameworkBackVersion',
  '/framework/front': 'frameworkFrontVersion',
  '/dataexploration': 'deVersion',
  '/datapreparation': 'dpVersion',
  '/investigation': 'invVersion',
  '/osint': 'osintVersion',
  '/backbone': 'backboneVersion',
};

const SQUAD_CONFIGS_262 = SQUAD_CONFIGS['26.2'];

const SQUAD_BUG_KEYWORDS = {
  'Framework Back':        ['administration', 'configuration', 'pelias'],
  'Framework Front':       ['administration', 'configuration'],
  'Data Exploration':      ['data exploration', 'exploration'],
  'Data Preparation':      ['data preparation', 'preparation'],
  'Investigation':         ['investigation'],
  'Innovation':            ['agentique', 'assistant', 'agentic', 'workplace'],
  'OSINT':                 ['osint'],
  'Backbone':              ['backbone'],
};

// Tous les pipeline IDs pour la recherche globale
const RF_PIPELINE_IDS = new Set(
  Object.values(ROBOT_PIPELINE_CONFIGS).flatMap(c => c.pipelineIds)
);
const ALL_PIPELINE_IDS = [...new Set([
  ...Object.values(READYAPI_PIPELINE_CONFIGS).flatMap(c => c.pipelineIds),
  ...Object.values(ROBOT_PIPELINE_CONFIGS).flatMap(c => c.pipelineIds)
])];
// Mapping pipelineId → version ReadyAPI (pour construire l'URL)
const READYAPI_PIPE_TO_VERSION = {};
for (const [version, config] of Object.entries(READYAPI_PIPELINE_CONFIGS)) {
  for (const id of config.pipelineIds) { READYAPI_PIPE_TO_VERSION[id] = version; }
}

const formatDuration = (seconds) => {
  const hours = Math.floor((Number(seconds) || 0) / 3600);
  const minutes = Math.floor(((Number(seconds) || 0) % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('fr-FR');
};

const buildAdoRunUrl = (buildId) => buildId
  ? `https://dev.azure.com/${azureDevOpsService.organization}/${azureDevOpsService.project}/_build/results?buildId=${buildId}`
  : null;

const buildStats = (pipelines) => {
  const totals = pipelines.reduce(
    (acc, p) => {
      acc.totalPassed += Number(p.passedTests) || 0;
      acc.totalFailed += Number(p.failedTests) || 0;
      acc.totalSkipped += Number(p.skippedTests) || 0;
      acc.totalTests += Number(p.totalTests) || 0;
      acc.totalDuration += Number(p.totalDuration) || 0;
      acc.pipelineCount += 1;
      return acc;
    },
    { totalPassed: 0, totalFailed: 0, totalSkipped: 0, totalTests: 0, totalDuration: 0, pipelineCount: 0 }
  );

  return {
    ...totals,
    averageSuccessRate: computeSuccessRate(totals.totalPassed, totals.totalTests, totals.totalSkipped),
    averageDuration: totals.pipelineCount ? totals.totalDuration / totals.pipelineCount : 0
  };
};

const SEV_CONFIG = {
  '1 - Critical': { label: 'Critique', dot: 'bg-red-500',   pill: 'bg-red-50 text-red-700 border border-red-200',    bar: 'bg-red-500'    },
  '2 - High':     { label: 'Haute',    dot: 'bg-orange-500', pill: 'bg-orange-50 text-orange-700 border border-orange-200', bar: 'bg-orange-400' },
  '3 - Medium':   { label: 'Moyenne',  dot: 'bg-yellow-400', pill: 'bg-yellow-50 text-yellow-700 border border-yellow-200', bar: 'bg-yellow-400' },
  '4 - Low':      { label: 'Basse',    dot: 'bg-blue-400',   pill: 'bg-blue-50 text-blue-700 border border-blue-200',  bar: 'bg-blue-400'   },
};

const STATE_STYLE = {
  'New':    'bg-emerald-100 text-emerald-700',
  'Active': 'bg-blue-100 text-blue-700',
  '1- New': 'bg-emerald-100 text-emerald-700',
  '2- Dev': 'bg-blue-100 text-blue-700',
  '3- Done':'bg-gray-100 text-gray-600',
  'Resolved':'bg-purple-100 text-purple-700',
  'Closed': 'bg-gray-100 text-gray-500',
};

const SevPill = ({ severity }) => {
  const cfg = SEV_CONFIG[severity] || { label: severity, dot: 'bg-gray-400', pill: 'bg-gray-100 text-gray-600 border border-gray-200' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.pill}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const BugCard = ({ bug }) => {
  const stateStyle = STATE_STYLE[bug.state] || 'bg-gray-100 text-gray-600';
  const squadPart = bug.areaPath ? bug.areaPath.split('\\').pop() : null;
  const initials = bug.assignedTo
    ? bug.assignedTo.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
    : '?';
  const tags = bug.tags ? bug.tags.split(';').map(t => t.trim()).filter(Boolean) : [];
  const daysAgo = bug.changedDate
    ? Math.floor((Date.now() - new Date(bug.changedDate).getTime()) / 86400000)
    : null;

  return (
    <div className="group bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all p-4 flex gap-4">
      <div className={`w-1 rounded-full flex-shrink-0 ${SEV_CONFIG[bug.severity]?.bar || 'bg-gray-300'}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <a href={bug.url} target="_blank" rel="noreferrer"
            className="font-semibold text-gray-900 hover:text-blue-600 transition-colors leading-snug line-clamp-2 text-sm">
            {bug.title}
          </a>
          <a href={bug.url} target="_blank" rel="noreferrer"
            className="flex-shrink-0 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2.5 py-1 rounded-lg transition-colors font-medium">
            Ouvrir <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <SevPill severity={bug.severity} />
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${stateStyle}`}>{bug.state}</span>
          {tags.map(t => (
            <span key={t} className="px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600 font-mono">{t}</span>
          ))}
        </div>
        <div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
          {squadPart && (
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
              {squadPart}
            </span>
          )}
          {bug.assignedTo && (
            <span className="flex items-center gap-1">
              <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center text-[9px] font-bold flex-shrink-0">
                {initials}
              </span>
              {bug.assignedTo}
            </span>
          )}
          {daysAgo !== null && (
            <span>
              {daysAgo === 0 ? "aujourd'hui" : daysAgo === 1 ? 'hier' : `il y a ${daysAgo}j`}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const SectionHeader = ({ title, subtitle, right }) => (
  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between pb-4 border-b-2 border-slate-100">
    <div>
      <h2 className="text-xl font-bold text-slate-800">{title}</h2>
      {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
    </div>
    {right}
  </div>
);

const formatVerifiedAt = (iso) =>
  new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

// Indicateur « suites vérifiées » d'une carte squad : vert quand tout le run
// courant a été revu, compteur gris si c'est partiel, rien si personne n'a commencé.
const SquadVerificationStatus = ({ suites }) => {
  const verifications = useSuiteVerifications();
  const knownFailures = useKnownFailures(suites);
  if (!verifications) return null;
  const { total, verified, allVerified } = summarizeVerifications(verifications, suites, knownFailures);
  if (total === 0 || verified.length === 0) return null;

  if (!allVerified) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-gray-500">
        <ShieldCheck className="w-3.5 h-3.5" />
        {verified.length}/{total} suites analysées
      </div>
    );
  }

  const manual = verified.filter(v => !v.auto);
  const noFailureCount = verified.filter(v => v.reason === 'no-failure').length;
  const knownBugsCount = verified.filter(v => v.reason === 'known-bugs').length;
  const verifiers = [...new Set(manual.map(v => v.verifiedBy))];
  const lastAt = manual.map(v => v.verifiedAt).sort().pop();
  const details = [
    manual.length > 0 && `Par ${verifiers.join(', ')} — le ${formatVerifiedAt(lastAt)}`,
    noFailureCount > 0 && (noFailureCount === 1 ? '1 run sans erreur' : `${noFailureCount} runs sans erreur`),
    knownBugsCount > 0 && (knownBugsCount === 1 ? '1 run aux échecs liés à un bug' : `${knownBugsCount} runs aux échecs liés à des bugs`),
  ].filter(Boolean).join(' · ');
  return (
    <div className="flex items-start gap-2 px-3 py-2 rounded-lg text-xs bg-green-50 text-green-800 border border-green-200">
      <ShieldCheck className="w-4 h-4 flex-shrink-0 text-green-600" />
      <div className="leading-tight">
        <div className="font-semibold">Toutes les suites analysées ({total}/{total})</div>
        <div className="text-green-700 mt-0.5 break-words">{details}</div>
      </div>
    </div>
  );
};

const SquadCard = ({ squad, navigate }) => {
  const rate = Number(squad.successRate) || 0;
  const barColor = rate >= 90 ? 'bg-green-500' : rate >= 70 ? 'bg-yellow-500' : 'bg-red-500';
  const borderColor = rate >= 90 ? 'border-green-500' : rate >= 70 ? 'border-yellow-500' : 'border-red-500';
  const rateColor = rate >= 90 ? 'text-green-600' : rate >= 70 ? 'text-yellow-600' : 'text-red-600';

  const lastExec = squad.executionDate
    ? new Date(squad.executionDate).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' })
    : null;
  const daysAgo = squad.executionDate
    ? Math.floor((Date.now() - new Date(squad.executionDate).getTime()) / 86400000)
    : null;

  return (
    <div className={`bg-white rounded-xl shadow-md overflow-hidden border-t-4 ${borderColor} ${squad.isRunning ? 'ring-2 ring-blue-300' : ''} flex flex-col`}>
      <div className="p-5 flex flex-col gap-4 flex-1">

        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-bold text-gray-900 text-base leading-tight">{squad.name}</h3>
            <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full mt-1.5 inline-block">
              {squad.env}
            </span>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {squad.isRunning && (
              <a
                href={squad.runningBuildUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-300 hover:bg-blue-200 transition-colors"
                title="Voir la pipeline en cours sur Azure DevOps"
              >
                <Loader2 className="w-3 h-3 animate-spin" />
                En cours
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
            <span className={`text-xl font-bold ${rateColor}`}>{rate.toFixed(1)}%</span>
          </div>
        </div>

        {/* Stats */}
        <div className={`grid ${squad.skippedTests > 0 ? 'grid-cols-4' : 'grid-cols-3'} divide-x divide-gray-100 text-center bg-gray-50 rounded-lg py-3`}>
          <div>
            <div className="text-2xl font-bold text-gray-900">{squad.totalTests}</div>
            <div className="text-xs text-gray-500 mt-0.5">Total</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">{squad.passedTests}</div>
            <div className="text-xs text-gray-500 mt-0.5">Passés</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-500">{squad.failedTests}</div>
            <div className="text-xs text-gray-500 mt-0.5">Échecs</div>
          </div>
          {squad.skippedTests > 0 && (
            <div>
              <div className="text-2xl font-bold text-gray-400">{squad.skippedTests}</div>
              <div className="text-xs text-gray-500 mt-0.5">Ignorés</div>
            </div>
          )}
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-100 rounded-full h-2">
          <div className={`${barColor} h-2 rounded-full transition-all`} style={{ width: `${Math.min(rate, 100)}%` }} />
        </div>

        {/* Meta */}
        <div className="space-y-1 text-xs text-gray-500">
          {lastExec && (
            <div className="flex justify-between">
              <span>Dernière exéc.</span>
              <span className="font-medium text-gray-700">
                {squad.lastExecutionBuildUrl ? (
                  <a
                    href={squad.lastExecutionBuildUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                    title="Voir le build sur Azure DevOps"
                  >
                    {lastExec}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ) : lastExec}
                {daysAgo !== null && daysAgo > 0 && (
                  <span className={`ml-1 ${daysAgo > 5 ? 'text-orange-500' : 'text-gray-400'}`}>
                    ({daysAgo === 1 ? 'hier' : `il y a ${daysAgo}j`})
                  </span>
                )}
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span>Durée totale</span>
            <span className="font-medium text-gray-700">{formatDuration(squad.totalDuration)}</span>
          </div>
        </div>

        {/* Coverage badges */}
        <div className="flex flex-wrap gap-1">
          {squad.types.map(t => (
            <span key={t} className="px-1.5 py-0.5 text-xs rounded bg-gray-100 text-gray-600 border border-gray-200">{t}</span>
          ))}
        </div>

        <SquadVerificationStatus suites={squad.verifiableSuites} />

        {/* CTA */}
        <button
          onClick={() => navigate(squad.route)}
          className="w-full mt-auto bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
        >
          <Eye className="w-4 h-4" />
          Voir l'équipe
        </button>
      </div>
    </div>
  );
};

const TestsOverviewPage = () => {
  const rrNavigate = useNavigate();
  const [selectedVersion, setSelectedVersion] = useState(() => {
    try {
      const stored = localStorage.getItem('testsOverviewVersion');
      return stored || 'develop';
    } catch {
      return 'develop';
    }
  });

  const navigateWithVersion = (route) => {
    const key = ROUTE_VERSION_KEYS[route];
    if (key) {
      const version = selectedVersion === 'develop' ? '26.1' : selectedVersion;
      try { localStorage.setItem(key, version); } catch {}
    }
    rrNavigate(route);
  };

  const [readyPipelines, setReadyPipelines] = useState([]);
  const [readyTrendData, setReadyTrendData] = useState({});
  const [readyLoading, setReadyLoading] = useState(true);
  const [readyError, setReadyError] = useState(null);

  const [allBugs, setAllBugs] = useState([]);
  const [showBugsModal, setShowBugsModal] = useState(false);

  const [robotPipelines, setRobotPipelines] = useState([]);
  const [robotTrendData, setRobotTrendData] = useState({});
  const [robotLoading, setRobotLoading] = useState(true);
  const [robotError, setRobotError] = useState(null);

  const [readyDays, setReadyDays] = useState(7);
  const [robotDays, setRobotDays] = useState(7);
  const [readyTrendLoading, setReadyTrendLoading] = useState(false);
  const [robotTrendLoading, setRobotTrendLoading] = useState(false);

  // Données squad — même logique que les pages d'équipe (getTestSuitesForBuild + filtre testSuiteName)
  const [squadSuiteData, setSquadSuiteData] = useState([]);
  const [squadSuiteLoading, setSquadSuiteLoading] = useState(false);
  const [rootCauseKpi, setRootCauseKpi] = useState(null);

  useEffect(() => {
    rootCauseService.getKpi(90).then(data => setRootCauseKpi(data)).catch(() => {});
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('testsOverviewVersion', selectedVersion);
    } catch {}
  }, [selectedVersion]);

  useEffect(() => {
    const fetchReadyApi = async () => {
      setReadyLoading(true);
      setReadyError(null);
      try {
        const config = READYAPI_PIPELINE_CONFIGS[selectedVersion] || READYAPI_PIPELINE_CONFIGS.develop;
        const pipelinesData = await azureDevOpsService.getBuildsData(config.pipelinesList);
        setReadyPipelines(pipelinesData);
      } catch (err) {
        console.error('Erreur ReadyAPI:', err);
        setReadyError("Impossible de charger les données ReadyAPI");
      } finally {
        setReadyLoading(false);
      }
    };
    fetchReadyApi();
  }, [selectedVersion]);

  useEffect(() => {
    const fetchReadyTrend = async () => {
      setReadyTrendLoading(true);
      try {
        const config = READYAPI_PIPELINE_CONFIGS[selectedVersion] || READYAPI_PIPELINE_CONFIGS.develop;
        const results = {};
        await Promise.all(config.pipelinesList.map(async (pipeline) => {
          try {
            results[pipeline.id] = await azureDevOpsService.getTrendDataForPipeline(pipeline.id, readyDays);
          } catch {
            results[pipeline.id] = [];
          }
        }));
        setReadyTrendData(results);
      } catch (err) {
        console.error('Erreur ReadyAPI trend:', err);
      } finally {
        setReadyTrendLoading(false);
      }
    };
    fetchReadyTrend();
  }, [selectedVersion, readyDays]);

  useEffect(() => {
    let mounted = true;
    const fetchAllBugs = async () => {
      try {
        const data = await azureDevOpsService.getBugsForAllSquads();
        if (mounted) setAllBugs(data);
      } catch (err) {
        console.error('Erreur récupération bugs :', err);
      }
    };

    fetchAllBugs();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const fetchRobot = async () => {
      const robotConfig = ROBOT_PIPELINE_CONFIGS[selectedVersion];
      if (!robotConfig) {
        setRobotPipelines([]);
        setRobotTrendData([]);
        setRobotError(null);
        setRobotLoading(false);
        return;
      }

      setRobotLoading(true);
      setRobotError(null);
      try {
        const pipelinesData = await azureDevOpsService.getBuildsData(robotConfig.pipelinesList);
        setRobotPipelines(pipelinesData);
      } catch (err) {
        console.error('Erreur RobotFramework:', err);
        setRobotError("Impossible de charger les données Robot Framework");
      } finally {
        setRobotLoading(false);
      }
    };
    fetchRobot();
  }, [selectedVersion]);

  useEffect(() => {
    const fetchRobotTrend = async () => {
      const robotConfig = ROBOT_PIPELINE_CONFIGS[selectedVersion];
      if (!robotConfig) { setRobotTrendData({}); return; }
      setRobotTrendLoading(true);
      try {
        const results = {};
        await Promise.all(robotConfig.pipelinesList.map(async (pipeline) => {
          try {
            results[pipeline.id] = await azureDevOpsService.getTrendDataForPipeline(pipeline.id, robotDays);
          } catch {
            results[pipeline.id] = [];
          }
        }));
        setRobotTrendData(results);
      } catch (err) {
        console.error('Erreur RobotFramework trend:', err);
      } finally {
        setRobotTrendLoading(false);
      }
    };
    fetchRobotTrend();
  }, [selectedVersion, robotDays]);

  useEffect(() => {
    if (selectedVersion !== 'develop' && selectedVersion !== '26.2') return;
    let mounted = true;
    const fetchSquadData = async () => {
      setSquadSuiteLoading(true);
      const activeSquadConfigs = selectedVersion === '26.2' ? SQUAD_CONFIGS_262 : SQUAD_CONFIGS_DEVELOP;
      try {
        const results = await Promise.all(activeSquadConfigs.map(async squad => {
          // Fetch RF suites — même logique que les pages d'équipe
          const suiteResults = await Promise.all(squad.rfConfigs.map(async cfg => {
            try {
              const buildsData = await azureDevOpsService.getRecentBuildsByPipeline(cfg.pipelineId, squad.deployPipeline ? 5 : 1);
              const builds = (buildsData[`pipeline-${cfg.pipelineId}`] || [])
                .filter(b => !squad.deployPipeline || b.totalTests > 0);
              if (!builds.length) return null;
              const latestBuild = builds[0];
              const testSuitesData = await azureDevOpsService.getTestSuitesForBuild(latestBuild.buildId);
              const allSuites = testSuitesData[`build-${latestBuild.buildId.replace('build-', '')}`] || [];
              const suite = allSuites.find(s => s.name === cfg.testSuiteName);
              if (!suite) return null;
              return { ...suite, pipelineId: cfg.pipelineId, buildId: latestBuild.buildId, executionDate: latestBuild.date };
            } catch {
              return null;
            }
          }));
          const foundSuites = suiteResults.filter(Boolean);

          let totalTests = foundSuites.reduce((s, r) => s + (r.totalTests || 0), 0);
          let passedTests = foundSuites.reduce((s, r) => s + (r.passed || 0), 0);
          let failedTests = foundSuites.reduce((s, r) => s + (r.failed || 0), 0);
          let skippedTests = foundSuites.reduce((s, r) => s + (r.skipped || 0), 0);
          let totalDuration = foundSuites.reduce((s, r) => s + (r.duration || 0), 0);
          // Conservés pour la synthèse des tests ignorés, qui a besoin de remonter aux résultats détaillés
          const buildIds = foundSuites.map(r => r.buildId).filter(Boolean);
          // Suites portant un bouton « vérifié » sur la page d'équipe, pour l'indicateur de la carte
          const verifiableSuites = foundSuites.map(r => ({ pipelineId: r.pipelineId, buildId: r.buildId, name: r.name, runId: r.runId, failed: r.failed, totalTests: r.totalTests, executionDate: r.executionDate }));

          // executionDate = date la plus récente parmi toutes les sources ; lastExecutionBuildUrl
          // pointe vers le build qui porte effectivement cette date (RF ou ReadyAPI).
          let executionDate = null;
          let lastExecutionBuildUrl = null;
          foundSuites.forEach(r => {
            if (r.executionDate && (!executionDate || r.executionDate > executionDate)) {
              executionDate = r.executionDate;
              lastExecutionBuildUrl = buildAdoRunUrl(r.buildId?.replace('build-', ''));
            }
          });

          // Fetch ReadyAPI si présent (même méthode que les pages d'équipe)
          let readyTestEnvironment = null;
          if (squad.readyApiPipelineId) {
            try {
              const readyBuilds = await azureDevOpsService.getBuildsData([
                { id: squad.readyApiPipelineId, name: 'ReadyAPI' }
              ]);
              const readyData = readyBuilds.find(p => p.id === squad.readyApiPipelineId);
              if (readyData) {
                if (readyData.lastBuildId) buildIds.push(readyData.lastBuildId);
                // name null : le nom ReadyAPI diffère ici de celui des pages d'équipe
                if (readyData.lastBuildId && !readyData.hasNoResults) {
                  verifiableSuites.push({ pipelineId: squad.readyApiPipelineId, buildId: readyData.lastBuildId, name: null, failed: readyData.failedTests, totalTests: readyData.totalTests, executionDate: readyData.executionDate });
                }
                totalTests += readyData.totalTests || 0;
                passedTests += readyData.passedTests || 0;
                failedTests += readyData.failedTests || 0;
                skippedTests += readyData.skippedTests || 0;
                totalDuration += readyData.totalDuration || 0;
                readyTestEnvironment = readyData.testEnvironment;
                if (readyData.executionDate && (!executionDate || readyData.executionDate > executionDate)) {
                  executionDate = readyData.executionDate;
                  lastExecutionBuildUrl = buildAdoRunUrl(readyData.lastBuildId);
                }
              }
            } catch {
              // ReadyAPI optionnel — on continue sans lui
            }
          }

          // Plateforme réelle remontée par les builds (templateParameters, cf. getEnvironmentFromBuild) ;
          // pas de repli sur le static `env` du config — tant qu'aucun run n'a chargé, on affiche N/A.
          const realEnvs = [...new Set(
            [...foundSuites.map(r => r.testEnvironment), readyTestEnvironment].filter(e => e && e !== 'Non défini')
          )];
          const env = realEnvs.length ? realEnvs.join(' / ') : (squad.deployPipeline ? squad.env : 'N/A');

          // Vérifie si une pipeline est en cours pour ce squad
          const allPipelineIds = [
            ...squad.rfConfigs.map(c => c.pipelineId),
            ...(squad.readyApiPipelineId ? [squad.readyApiPipelineId] : [])
          ];
          const runningChecks = await Promise.all(
            allPipelineIds.map(id => azureDevOpsService.hasRunningBuild(id).catch(() => null))
          );
          const runningBuild = runningChecks.find(Boolean);
          const isRunning = Boolean(runningBuild);

          const successRate = computeSuccessRate(passedTests, totalTests, skippedTests);
          return { ...squad, env, totalTests, passedTests, failedTests, skippedTests, totalDuration, successRate, executionDate, lastExecutionBuildUrl, isRunning, runningBuildUrl: runningBuild?.url || null, buildIds, verifiableSuites };
        }));
        if (mounted) setSquadSuiteData(results);
      } catch (err) {
        console.error('Erreur chargement données squads:', err);
      } finally {
        if (mounted) setSquadSuiteLoading(false);
      }
    };
    fetchSquadData();
    return () => { mounted = false; };
  }, [selectedVersion]);

  const totalSkipped = useMemo(
    () => squadSuiteData.reduce((sum, s) => sum + (s.skippedTests || 0), 0),
    [squadSuiteData]
  );

  // Seuls les squads qui ont réellement des ignorés sont interrogés — chaque build
  // est étiqueté avec son squad pour que la synthèse globale reste lisible.
  const skippedBuilds = useMemo(
    () => squadSuiteData
      .filter(squad => (squad.skippedTests || 0) > 0)
      .flatMap(squad => (squad.buildIds || []).map(buildId => ({ buildId, label: squad.name }))),
    [squadSuiteData]
  );

  const readyStats = useMemo(() => buildStats(readyPipelines), [readyPipelines]);
  const robotStats = useMemo(() => buildStats(robotPipelines), [robotPipelines]);

  const robotEnabled = useMemo(() => Boolean(ROBOT_PIPELINE_CONFIGS[selectedVersion]), [selectedVersion]);

  const robotDomainGroups = useMemo(() => {
    const config = ROBOT_PIPELINE_CONFIGS[selectedVersion];
    if (!config || !robotPipelines.length) return [];
    const hasDomain = config.pipelinesList.some(p => p.domain);
    if (!hasDomain) {
      return robotPipelines.map(p => ({
        domain: p.name, pipelineIds: [p.id],
        pipelines: [p], ...p,
        env: (p.testEnvironment && p.testEnvironment !== 'Non défini') ? p.testEnvironment : 'N/A',
        primaryId: p.id,
        runningBuildUrl: p.isRunning ? buildAdoRunUrl(p.lastBuildId) : null,
        executionDateBuildUrl: buildAdoRunUrl(p.lastBuildId)
      }));
    }
    const domainMap = new Map();
    config.pipelinesList.forEach(({ id, domain }) => {
      if (!domainMap.has(domain)) domainMap.set(domain, { domain, pipelineIds: [] });
      domainMap.get(domain).pipelineIds.push(id);
    });
    return Array.from(domainMap.values()).map(({ domain, pipelineIds }) => {
      const pipes = robotPipelines.filter(p => pipelineIds.includes(p.id));
      const totalPassed = pipes.reduce((s, p) => s + (Number(p.passedTests) || 0), 0);
      const totalFailed = pipes.reduce((s, p) => s + (Number(p.failedTests) || 0), 0);
      const totalSkipped = pipes.reduce((s, p) => s + (Number(p.skippedTests) || 0), 0);
      const totalTests = totalPassed + totalFailed + totalSkipped;
      const successRate = computeSuccessRate(totalPassed, totalTests, totalSkipped).toFixed(1);
      const totalDuration = pipes.reduce((s, p) => s + (Number(p.totalDuration) || 0), 0);
      // Le pipe le plus récent détermine executionDate ; on garde son buildId pour lier la date affichée.
      const latestPipe = pipes.filter(p => p.executionDate).sort((a, b) => (b.executionDate > a.executionDate ? 1 : -1))[0] || null;
      const executionDate = latestPipe?.executionDate || null;
      const executionDateBuildUrl = buildAdoRunUrl(latestPipe?.lastBuildId);
      const isRunning = pipes.some(p => p.isRunning);
      const lastBuildId = pipes.map(p => p.lastBuildId).filter(Boolean).join(' / ');
      const runningBuildUrl = buildAdoRunUrl(pipes.find(p => p.isRunning)?.lastBuildId);
      // Plateforme réelle remontée par le build (templateParameters) ; pas de repli sur une
      // valeur statique — tant qu'aucun run n'a chargé, on affiche N/A.
      const realEnvs = [...new Set(pipes.map(p => p.testEnvironment).filter(e => e && e !== 'Non défini'))];
      const resolvedEnv = realEnvs.length ? realEnvs.join(' / ') : 'N/A';
      return { domain, env: resolvedEnv, pipelineIds, pipelines: pipes, primaryId: pipelineIds[0],
        passedTests: totalPassed, failedTests: totalFailed, skippedTests: totalSkipped, totalTests, successRate,
        totalDuration, executionDate, executionDateBuildUrl, isRunning, runningBuildUrl, lastBuildId };
    });
  }, [robotPipelines, selectedVersion]);

  const squadData = useMemo(() => {
    if (selectedVersion !== 'develop' && selectedVersion !== '26.2') return [];
    return squadSuiteData;
  }, [squadSuiteData, selectedVersion]);

  const globalStats = useMemo(() => {
    // En version develop/26.2, on utilise squadSuiteData (même source que les pages d'équipe)
    if ((selectedVersion === 'develop' || selectedVersion === '26.2') && squadSuiteData.length > 0) {
      return buildStats(squadSuiteData);
    }
    return buildStats([...(readyPipelines || []), ...(robotEnabled ? robotPipelines || [] : [])]);
  }, [readyPipelines, robotPipelines, robotEnabled, squadSuiteData, selectedVersion]);

  const criticalBugsCount = useMemo(() => {
    if (selectedVersion === '25.1') return 0;
    return (allBugs || []).filter((bug) => String(bug.severity) === String('1 - Critical')).length;
  }, [allBugs, selectedVersion]);

  const loading = readyLoading || (robotEnabled && robotLoading);
  const anyError = readyError || (robotEnabled ? robotError : null);

  const readyStatus = useMemo(
    () => getStatusFromSuccessRate(readyStats.averageSuccessRate, 0),
    [readyStats.averageSuccessRate]
  );
  const robotStatus = useMemo(
    () => getStatusFromSuccessRate(robotStats.averageSuccessRate, 0),
    [robotStats.averageSuccessRate]
  );
  const globalStatus = useMemo(
    () => getStatusFromSuccessRate(globalStats.averageSuccessRate, (selectedVersion === 'develop' || selectedVersion === '26.2') ? criticalBugsCount : 0),
    [globalStats.averageSuccessRate, criticalBugsCount, selectedVersion]
  );


  const returnToParam = useMemo(() => encodeURIComponent('/tests'), []);

  const [isSendingToSlack, setIsSendingToSlack] = useState(false);
  const [triggerLoading, setTriggerLoading] = useState(new Set());

  // Génère une carte HTML propre avec styles inline et la capture avec html2canvas
  const captureCleanDashboardCard = async (data) => {
    const { version, globalRate, globalStatusLabel, totalTests, totalPassed, totalFailed, squads = [], bugs } = data;

    const rateColor = (r) => {
      const n = parseFloat(r);
      if (n >= 95) return '#16a34a';
      if (n >= 85) return '#d97706';
      return '#dc2626';
    };

    const statusColor = (label = '') => {
      const l = label.toLowerCase();
      if (l.includes('stable') && !l.includes('pas')) return '#16a34a';
      if (l.includes('warning') || l.includes('partiel')) return '#d97706';
      return '#dc2626';
    };

    const now = new Date();
    const dateStr = now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    const gc = rateColor(globalRate);
    const sc = statusColor(globalStatusLabel);

    const squadsHtml = squads.map(sq => {
      const c = rateColor(sq.successRate);
      return `<div style="flex:1;min-width:200px;max-width:240px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:16px;border-top:4px solid ${c};">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;">
          <div style="font-weight:700;font-size:14px;color:#1e293b;">${sq.name}</div>
          <div style="font-size:20px;font-weight:800;color:${c};">${parseFloat(sq.successRate).toFixed(1)}%</div>
        </div>
        <div style="display:flex;gap:16px;">
          <div style="text-align:center;"><div style="font-size:18px;font-weight:700;color:#1e293b;">${sq.totalTests}</div><div style="font-size:10px;color:#64748b;margin-top:2px;">Total</div></div>
          <div style="text-align:center;"><div style="font-size:18px;font-weight:700;color:#16a34a;">${sq.passedTests}</div><div style="font-size:10px;color:#64748b;margin-top:2px;">Passés</div></div>
          <div style="text-align:center;"><div style="font-size:18px;font-weight:700;color:#dc2626;">${sq.failedTests}</div><div style="font-size:10px;color:#64748b;margin-top:2px;">Échecs</div></div>${sq.skippedTests > 0 ? `<div style="text-align:center;"><div style="font-size:18px;font-weight:700;color:#94a3b8;">${sq.skippedTests}</div><div style="font-size:10px;color:#64748b;margin-top:2px;">Ignorés</div></div>` : ''}
        </div>
      </div>`;
    }).join('');

    const bugsHtml = (bugs && bugs.total !== undefined) ? `
      <div style="background:#fff7ed;border:1px solid #fed7aa;border-radius:10px;padding:18px;margin-top:20px;">
        <div style="font-size:15px;font-weight:700;color:#9a3412;margin-bottom:12px;">🐛 Bugs actifs — ${bugs.total} au total</div>
        <div style="display:flex;gap:10px;flex-wrap:wrap;">
          ${bugs.critical ? `<div style="background:#fee2e2;padding:6px 14px;border-radius:6px;font-weight:700;color:#991b1b;font-size:13px;">${bugs.critical} Critique${bugs.critical > 1 ? 's' : ''}</div>` : ''}
          ${bugs.high ? `<div style="background:#ffedd5;padding:6px 14px;border-radius:6px;font-weight:700;color:#c2410c;font-size:13px;">${bugs.high} Majeur${bugs.high > 1 ? 's' : ''}</div>` : ''}
          ${bugs.medium ? `<div style="background:#fefce8;padding:6px 14px;border-radius:6px;font-weight:700;color:#a16207;font-size:13px;">${bugs.medium} Moyen${bugs.medium > 1 ? 's' : ''}</div>` : ''}
          ${bugs.low ? `<div style="background:#f1f5f9;padding:6px 14px;border-radius:6px;font-weight:700;color:#475569;font-size:13px;">${bugs.low} Mineur${bugs.low > 1 ? 's' : ''}</div>` : ''}
        </div>
      </div>` : '';

    const html = `<div style="width:860px;background:#ffffff;padding:28px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;color:#1e293b;font-size:14px;line-height:1.5;">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:20px;padding-bottom:16px;border-bottom:2px solid #e2e8f0;">
        <div>
          <div style="font-size:24px;font-weight:800;color:#0f172a;">📊 Vue Globale QA</div>
          <div style="font-size:12px;color:#64748b;margin-top:3px;">Version ${version} · ${dateStr} · ${timeStr}</div>
        </div>
        <div style="background:${sc}18;color:${sc};padding:5px 13px;border-radius:20px;font-weight:600;font-size:12px;border:1px solid ${sc}44;">${globalStatusLabel}</div>
      </div>
      <div style="display:flex;gap:12px;margin-bottom:24px;">
        <div style="flex:1;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:10px;padding:16px;">
          <div style="font-size:11px;font-weight:600;text-transform:uppercase;color:#16a34a;letter-spacing:0.5px;">Tests passés</div>
          <div style="font-size:32px;font-weight:800;color:#15803d;margin-top:4px;">${totalPassed}</div>
        </div>
        <div style="flex:1;background:#fef2f2;border:1px solid #fecaca;border-radius:10px;padding:16px;">
          <div style="font-size:11px;font-weight:600;text-transform:uppercase;color:#dc2626;letter-spacing:0.5px;">Tests échoués</div>
          <div style="font-size:32px;font-weight:800;color:#b91c1c;margin-top:4px;">${totalFailed}</div>
        </div>
        <div style="flex:1;background:#eff6ff;border:1px solid #bfdbfe;border-radius:10px;padding:16px;">
          <div style="font-size:11px;font-weight:600;text-transform:uppercase;color:#2563eb;letter-spacing:0.5px;">Total exécutés</div>
          <div style="font-size:32px;font-weight:800;color:#1d4ed8;margin-top:4px;">${totalTests}</div>
        </div>
        <div style="flex:1;background:${gc}11;border:1px solid ${gc}33;border-radius:10px;padding:16px;">
          <div style="font-size:11px;font-weight:600;text-transform:uppercase;color:${gc};letter-spacing:0.5px;">Taux de réussite</div>
          <div style="font-size:32px;font-weight:800;color:${gc};margin-top:4px;">${parseFloat(globalRate).toFixed(1)}%</div>
        </div>
      </div>
      <div style="font-size:16px;font-weight:700;color:#0f172a;margin-bottom:3px;">Résultats par squad</div>
      <div style="font-size:11px;color:#94a3b8;margin-bottom:14px;">Dernières exécutions Robot Framework et ReadyAPI</div>
      <div style="display:flex;flex-wrap:wrap;gap:10px;margin-bottom:4px;">${squadsHtml}</div>
      ${bugsHtml}
      <div style="text-align:center;color:#94a3b8;font-size:11px;margin-top:20px;padding-top:14px;border-top:1px solid #f1f5f9;">QA Dashboard · ${dateStr} à ${timeStr}</div>
    </div>`;

    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'position:fixed;top:0;left:-9999px;z-index:-1;';
    wrapper.innerHTML = html;
    document.body.appendChild(wrapper);

    try {
      const cardEl = wrapper.firstElementChild;
      const canvas = await html2canvas(cardEl, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        width: 860,
        windowWidth: 860,
        backgroundColor: '#ffffff',
        letterRendering: true,
      });
      return await new Promise(resolve => canvas.toBlob(resolve, 'image/png', 1.0));
    } finally {
      document.body.removeChild(wrapper);
    }
  };

  // Fonction pour envoyer le dashboard vers Slack
  const handleSendToSlack = async () => {
    try {
      setIsSendingToSlack(true);
      toast.loading('Génération du récapitulatif...', { id: 'slack' });

      const dashboardData = {
        version: selectedVersion,
        globalRate: Number(globalStats.averageSuccessRate).toFixed(1),
        globalStatusLabel: globalStatus?.label || 'N/A',
        totalTests: globalStats.totalTests || 0,
        totalPassed: globalStats.totalPassed || 0,
        totalFailed: globalStats.totalFailed || 0,
        totalSkipped: globalStats.totalSkipped || 0,
        squads: squadData.map(s => ({
          name: s.name,
          successRate: Number(s.successRate || 0).toFixed(1),
          totalTests: s.totalTests || 0,
          passedTests: s.passedTests || 0,
          failedTests: s.failedTests || 0,
          skippedTests: s.skippedTests || 0,
        })),
        bugs: {
          total: allBugs.length,
          critical: allBugs.filter(b => b.severity === '1 - Critical').length,
          high: allBugs.filter(b => b.severity === '2 - High').length,
          medium: allBugs.filter(b => b.severity === '3 - Medium').length,
          low: allBugs.filter(b => b.severity === '4 - Low').length,
        },
      };

      // Capture via une carte HTML dédiée (styles inline) pour un rendu propre
      const blob = await captureCleanDashboardCard(dashboardData);

      toast.loading('Envoi vers Slack...', { id: 'slack' });
      await sendDashboardToSlack(dashboardData, blob);
      toast.success(`Récapitulatif envoyé vers Slack ! 🎉`, { id: 'slack', duration: 4000 });
    } catch (err) {
      console.error('Erreur envoi Slack:', err);
      toast.error(`Erreur: ${err.message}`, { id: 'slack' });
    } finally {
      setIsSendingToSlack(false);
    }
  };

  const handleTriggerPipeline = async (pipelineId) => {
    setTriggerLoading(prev => new Set([...prev, pipelineId]));
    try {
      await azureDevOpsService.triggerPipeline(pipelineId);
      toast.success('Pipeline lancé avec succès');
    } catch (err) {
      toast.error(`Erreur lors du lancement : ${err.message}`);
    } finally {
      setTriggerLoading(prev => { const s = new Set(prev); s.delete(pipelineId); return s; });
    }
  };

  const bugsBySquad = useMemo(() => {
    const map = {};
    allBugs.forEach(b => {
      const haystack = `${b.areaPath} ${b.tags}`.toLowerCase();
      Object.entries(SQUAD_BUG_KEYWORDS).forEach(([squad, keywords]) => {
        if (keywords.some(kw => haystack.includes(kw))) {
          map[squad] = (map[squad] || 0) + 1;
        }
      });
    });
    return map;
  }, [allBugs]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Screenshot / Copy buttons (top-right fixed) */}
      <div className="fixed top-20 right-4 z-[9999] flex flex-col gap-2">
        <button
          onClick={async () => {
            try {
              toast.loading('Capture en cours...', { id: 'screenshot-db' });
              const el = document.documentElement;
              const canvas = await html2canvas(el, { scale: 1 });
              const dataUrl = canvas.toDataURL('image/png');
              const link = document.createElement('a');
              link.href = dataUrl;
              link.download = `vue-globale-${Date.now()}.png`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              toast.success('Screenshot téléchargé', { id: 'screenshot-db' });
            } catch (err) {
              console.error(err);
              toast.error('Erreur capture', { id: 'screenshot-db' });
            }
          }}
          className="px-2 py-2 rounded flex items-center justify-center text-blue-600 hover:text-blue-800 bg-white shadow-md"
          title="Capture d'écran"
          aria-label="Capture d'écran Vue globale"
        >
          <Camera className="w-5 h-5" />
        </button>

        <button
          onClick={async () => {
            try {
              toast.loading('Génération et copie du lien...', { id: 'link-db' });
              const body = document.body;
              const htmlEl = document.documentElement;
              const fullHeight = Math.max(body.scrollHeight, body.offsetHeight, htmlEl.clientHeight, htmlEl.scrollHeight, htmlEl.offsetHeight);
              const canvas = await html2canvas(body, {
                scrollY: -window.scrollY,
                scrollX: -window.scrollX,
                height: fullHeight,
                windowHeight: fullHeight,
                width: htmlEl.scrollWidth,
                scale: 1,
                useCORS: true,
                allowTaint: true,
                logging: false
              });
              const dataUrl = canvas.toDataURL('image/png');
              const links = Array.from(document.querySelectorAll('a[href]'))
                .map((a) => {
                  try {
                    const r = a.getBoundingClientRect();
                    const left = r.left + window.scrollX;
                    const top = r.top + window.scrollY;
                    return {
                      href: a.href,
                      target: a.target || '_blank',
                      left: Math.round(left),
                      top: Math.round(top),
                      width: Math.round(r.width),
                      height: Math.round(r.height)
                    };
                  } catch (e) {
                    return null;
                  }
                })
                .filter(Boolean)
                .filter(l => l.width > 2 && l.height > 2);

              const winHtmlParts = [];
              winHtmlParts.push('<!doctype html><html><head><meta charset="utf-8"><title>Vue globale avec liens</title>');
              winHtmlParts.push('<meta name="viewport" content="width=device-width, initial-scale=1">');
              winHtmlParts.push('<style>body{margin:0;background:#111;color:#111} .container{position:relative;}\nimg{display:block;border:0}\n.overlay{position:absolute;display:block;background:transparent}\n.overlay:hover{outline:2px solid rgba(59,130,246,0.35)}\n</style></head><body>');
              winHtmlParts.push(`<div class="container" style="width:${canvas.width}px;height:${canvas.height}px">`);
              winHtmlParts.push(`<img src="${dataUrl}" width="${canvas.width}" height="${canvas.height}" alt="screenshot"/>`);
              const scaleX = canvas.width / (htmlEl.scrollWidth || canvas.width);
              const scaleY = canvas.height / (fullHeight || canvas.height);
              const overlayVerticalOffset = -58;
              links.forEach((l, idx) => {
                const href = String(l.href).replace(/"/g, '%22');
                const leftS = Math.round(l.left * scaleX);
                const topS = Math.round(l.top * scaleY) + overlayVerticalOffset;
                const widthS = Math.max(1, Math.round(l.width * scaleX));
                const heightS = Math.max(1, Math.round(l.height * scaleY));
                winHtmlParts.push(`<a class="overlay" href="${href}" target="${l.target}" style="left:${leftS}px;top:${topS}px;width:${widthS}px;height:${heightS}px" title="Lien ${idx+1}"></a>`);
              });
              winHtmlParts.push('</div></body></html>');
              const htmlContent = winHtmlParts.join('\n');

              const blob = new Blob([htmlContent], { type: 'text/html' });
              const url = URL.createObjectURL(blob);
              await navigator.clipboard.writeText(url);
              setTimeout(() => { try { URL.revokeObjectURL(url); } catch(e){} }, 1000 * 60 * 10);
              toast.success('Lien du snapshot copié dans le presse-papier', { id: 'link-db' });
            } catch (err) {
              console.error('Erreur copie lien snapshot:', err);
              toast.error('Erreur lors de la copie du lien', { id: 'link-db' });
            }
          }}
          className="px-2 py-2 rounded flex items-center justify-center text-blue-600 hover:text-blue-800 bg-white shadow-md"
          title="Copier le lien du snapshot"
          aria-label="Copier le lien snapshot Vue globale"
        >
          <Copy className="w-5 h-5" />
        </button>

        <button
          onClick={handleSendToSlack}
          disabled={isSendingToSlack}
          className={`px-2 py-2 rounded flex items-center justify-center bg-white shadow-md ${
            isSendingToSlack 
              ? 'text-gray-400 cursor-not-allowed' 
              : 'text-green-600 hover:text-green-800'
          }`}
          title="Envoyer vers Slack"
          aria-label="Envoyer vers Slack Vue globale"
        >
          {isSendingToSlack ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </button>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-10">
        <div className="bg-white rounded-xl shadow-md p-6 space-y-4 border-t-4 border-blue-600">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Vue globale</h1>
              <p className="text-sm text-slate-500 mt-1">Consolidation ReadyAPI et Robot Framework</p>
            </div>
            <div className="flex items-center gap-3">
              {loading ? (
                <div className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 animate-pulse">
                  Chargement...
                </div>
              ) : (
                <div className={`text-xs px-2 py-1 rounded ${
                  globalStatus.color === 'green'
                    ? 'bg-green-100 text-green-700'
                    : globalStatus.color === 'yellow'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-red-100 text-red-700'
                }`}>
                  {globalStatus.label}
                </div>
              )}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Version</label>
                <select
                  value={selectedVersion}
                  onChange={(e) => setSelectedVersion(e.target.value)}
                  className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                >
                  {Object.keys(READYAPI_PIPELINE_CONFIGS).map((key) => (
                    <option key={key} value={key}>
                      {READYAPI_PIPELINE_CONFIGS[key].label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Barre de recherche globale — cherche dans ReadyAPI + RobotFramework */}
          <TestCaseSearchBar
            placeholder="Chercher un test case dans ReadyAPI et Robot Framework..."
            onSelect={async (result) => {
              try {
                const location = await azureDevOpsService.findTestCaseLocation(
                  result.id, result.title, ALL_PIPELINE_IDS
                );
                if (location) {
                  const isRF = RF_PIPELINE_IDS.has(location.pipelineId);
                  if (isRF) {
                    rrNavigate(
                      `/robotframework/pipeline/${location.pipelineId}/build/${location.buildId}/run/${location.runId}/testsuite/${encodeURIComponent(location.suiteName)}?q=${encodeURIComponent(result.title)}`
                    );
                  } else {
                    const version = READYAPI_PIPE_TO_VERSION[location.pipelineId] || selectedVersion;
                    rrNavigate(
                      `/readyapi/pipeline/${location.pipelineId}/build/${location.buildId}/run/${location.runId}/testsuite/${encodeURIComponent(location.suiteName)}?version=${version}&config=new&q=${encodeURIComponent(result.title)}`
                    );
                  }
                  return;
                }
              } catch (e) {
                console.warn('findTestCaseLocation Vue globale échec:', e);
              }
            }}
          />
        </div>

        {loading && <div className="text-center py-6">Chargement des données...</div>}
        {anyError && <div className="text-center py-6 text-red-600">{anyError}</div>}

        {!loading && !anyError && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tests Passés</p>
                    <p className="text-3xl font-bold text-slate-800 mt-1">{globalStats.totalPassed}</p>
                  </div>
                  <div className="bg-green-50 rounded-full p-3">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tests Échoués</p>
                    <p className="text-3xl font-bold text-slate-800 mt-1">{globalStats.totalFailed}</p>
                  </div>
                  <div className="bg-red-50 rounded-full p-3">
                    <XCircle className="w-6 h-6 text-red-500" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Taux de Réussite</p>
                    <p className="text-3xl font-bold text-slate-800 mt-1">
                      {typeof globalStats.averageSuccessRate === 'number'
                        ? globalStats.averageSuccessRate.toFixed(1)
                        : '0.0'}%
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-full p-3">
                    <TrendingUp className="w-6 h-6 text-blue-500" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Durée Totale</p>
                    <p className="text-3xl font-bold text-slate-800 mt-1">{formatDuration(globalStats.totalDuration)}</p>
                  </div>
                  <div className="bg-purple-50 rounded-full p-3">
                    <Clock className="w-6 h-6 text-purple-500" />
                  </div>
                </div>
              </div>
            </div>

            {/* Section pipelines en cours d'exécution */}
            {(() => {
              const running = [
                ...readyPipelines.filter(p => p.isRunning).map(p => ({ ...p, type: 'ReadyAPI' })),
                ...(robotEnabled ? robotPipelines.filter(p => p.isRunning).map(p => ({ ...p, type: 'Robot Framework' })) : [])
              ];
              if (running.length === 0) return null;
              return (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                    <span className="text-sm font-semibold text-blue-800">
                      {running.length} pipeline{running.length > 1 ? 's' : ''} en cours d'exécution
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {running.map(p => (
                      <a
                        key={p.id}
                        href={buildAdoRunUrl(p.lastBuildId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-200 transition-colors"
                      >
                        <Play className="w-3 h-3" />
                        {p.name}
                        <span className="text-blue-500">({p.type})</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ))}
                  </div>
                </div>
              );
            })()}

            {(selectedVersion === 'develop' || selectedVersion === '26.2') ? (
              <section className="space-y-4">
                <SectionHeader
                  title="Résultats par squad"
                  subtitle={`Synthèse des dernières exécutions Robot Framework — ${selectedVersion === '26.2' ? '26.2' : '26.1'}.`}
                />
                {squadSuiteLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                    {(selectedVersion === '26.2' ? SQUAD_CONFIGS_262 : SQUAD_CONFIGS_DEVELOP).map(s => (
                      <div key={s.name} className="bg-white rounded-xl shadow-md border-t-4 border-gray-200 p-5 space-y-4 animate-pulse">
                        <div className="h-5 bg-gray-200 rounded w-2/3" />
                        <div className="h-14 bg-gray-100 rounded" />
                        <div className="h-2 bg-gray-200 rounded" />
                        <div className="h-10 bg-blue-100 rounded" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                    {squadData.map(squad => (
                      <SquadCard key={squad.name} squad={squad} navigate={navigateWithVersion} />
                    ))}
                  </div>
                )}

                {!squadSuiteLoading && totalSkipped > 0 && (
                  <div className="mt-6">
                    <SkippedTestsPanel
                      builds={skippedBuilds}
                      expectedTotal={totalSkipped}
                      title="Tests ignorés — toutes squads"
                    />
                  </div>
                )}

                {!squadSuiteLoading && squadData.length > 0 && (
                  <div className="mt-6">
                    <SectionHeader
                      title="Comparaison par squad"
                      subtitle="Vue synthétique des résultats de toutes les squads"
                    />
                    <div className="overflow-x-auto mt-4">
                    <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Squad</th>
                          <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Global %</th>
                          <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Bugs</th>
                          <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide" title="Échecs classifiés comme défaut du test lui-même">RC Test</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(selectedVersion === '26.2' ? SQUAD_CONFIGS_262 : SQUAD_CONFIGS_DEVELOP).map(cfg => {
                          const squadRow = squadData.find(s => s.name === cfg.name);
                          const globalRate = squadRow ? Math.round(squadRow.successRate || 0) : null;
                          const bugsCount = bugsBySquad[cfg.name] || 0;
                          const rcTestCount = rootCauseKpi
                            ? (rootCauseKpi.bySquad?.[cfg.name]?.Test || 0)
                            : null;

                          const rateColor = (r) => r === null ? 'text-gray-400' : r >= 90 ? 'text-green-600 font-semibold' : r >= 70 ? 'text-yellow-600 font-semibold' : 'text-red-600 font-semibold';
                          return (
                            <tr key={cfg.name} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-2.5 font-medium text-gray-800">{cfg.name}</td>
                              <td className={`px-4 py-2.5 text-center ${rateColor(globalRate)}`}>{globalRate !== null ? `${globalRate}%` : '—'}</td>
                              <td className={`px-4 py-2.5 text-center ${bugsCount > 0 ? 'text-orange-600 font-semibold' : 'text-gray-400'}`}>{bugsCount}</td>
                              <td className={`px-4 py-2.5 text-center ${rcTestCount > 0 ? 'text-purple-600 font-semibold' : 'text-gray-400'}`}>{rcTestCount !== null ? rcTestCount : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  </div>
                )}
              </section>
            ) : (
              <>
            <section className="space-y-6">
              <SectionHeader
                title="ReadyAPI"
                subtitle="Résultats par pipeline pour la version sélectionnée."
                right={
                  <div className={`text-xs px-2 py-1 rounded ${
                    readyStatus.color === 'green'
                      ? 'bg-green-100 text-green-700'
                      : readyStatus.color === 'yellow'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                  }`}>
                    {readyStatus.label}
                  </div>
                }
              />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-slate-50 rounded-xl p-4 border border-green-200 border-l-4 border-l-green-500">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tests Passés</p>
                  <p className="text-2xl font-bold text-slate-800 mt-1">{readyStats.totalPassed}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 border border-red-200 border-l-4 border-l-red-500">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tests Échoués</p>
                  <p className="text-2xl font-bold text-slate-800 mt-1">{readyStats.totalFailed}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 border border-blue-200 border-l-4 border-l-blue-500">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Taux de Réussite</p>
                  <p className="text-2xl font-bold text-slate-800 mt-1">
                    {typeof readyStats.averageSuccessRate === 'number'
                      ? readyStats.averageSuccessRate.toFixed(1)
                      : '0.0'}%
                  </p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 border border-purple-200 border-l-4 border-l-purple-500">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Durée Totale</p>
                  <p className="text-2xl font-bold text-slate-800 mt-1">{formatDuration(readyStats.totalDuration)}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6">
                {readyPipelines.map((pipeline) => {
                  const pipelineStatus = getStatusFromSuccessRate(Number(pipeline.successRate) || 0);
                  const trendData = readyTrendData[pipeline.id] || [];
                  const lastRun = trendData[trendData.length - 1];
                  const maxTotal = trendData.length > 0 ? Math.max(...trendData.map(d => d.total)) : 0;
                  return (
                  <div key={pipeline.id} className={`rounded-xl shadow-md overflow-hidden ${pipeline.isRunning ? 'bg-blue-50 border border-blue-200' : 'bg-white'}`}>
                    <div className="flex">
                      <div className="flex-1 p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-900">{pipeline.name}</h3>
                          <div className="flex items-center gap-2">
                            {pipeline.isRunning && (
                              <a
                                href={buildAdoRunUrl(pipeline.lastBuildId)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-200 transition-colors"
                                title="Voir la pipeline en cours sur Azure DevOps"
                              >
                                <Loader2 className="w-3 h-3 animate-spin" />
                                En cours
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${
                              pipelineStatus.color === 'green'
                                ? 'bg-green-100 text-green-800 border-green-300'
                                : pipelineStatus.color === 'yellow'
                                  ? 'bg-yellow-100 text-yellow-800 border-yellow-300'
                                  : 'bg-red-100 text-red-800 border-red-300'
                            }`}>
                              {pipelineStatus.color === 'green' ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                              {pipelineStatus.label}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-3">
                          {pipeline.executionDate && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Date d'exécution:</span>
                              <span className="font-medium">
                                <a
                                  href={buildAdoRunUrl(pipeline.lastBuildId)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                                  title="Voir le build sur Azure DevOps"
                                >
                                  {formatDate(pipeline.executionDate)}
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Dernier Build:</span>
                            <span className="font-medium">{pipeline.lastBuildId}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Tests:</span>
                            <span className="font-medium">
                              <span className="text-green-600">{pipeline.passedTests}</span> /
                              <span className="text-red-600">{pipeline.failedTests}</span> /
                              {pipeline.skippedTests > 0 && <><span className="text-gray-500">{pipeline.skippedTests}</span> / </>}
                              <span className="text-gray-900">{pipeline.totalTests}</span>
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Durée:</span>
                            <span className="font-medium">{formatDuration(pipeline.totalDuration)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Taux de réussite:</span>
                            <span className="font-medium">{pipeline.successRate}</span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full transition-all ${
                                Number(pipeline.successRate) >= 90 ? 'bg-green-500' :
                                Number(pipeline.successRate) >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${Math.min(Number(pipeline.successRate) || 0, 100)}%` }}
                            ></div>
                          </div>
                          <div className="flex gap-2 mt-4">
                            <button
                              onClick={() => handleTriggerPipeline(pipeline.id)}
                              disabled={triggerLoading.has(pipeline.id)}
                              className="bg-gray-100 text-gray-700 px-3 py-2.5 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-1.5 text-sm font-medium border border-gray-200 disabled:opacity-50"
                            >
                              {triggerLoading.has(pipeline.id) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                              Lancer
                            </button>
                            <Link
                              to={`/readyapi/pipeline/${pipeline.id}?version=${selectedVersion}&returnTo=${returnToParam}&config=new`}
                              className="flex-1 bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
                            >
                              <Eye className="w-4 h-4" />
                              Voir Détails
                            </Link>
                          </div>
                        </div>
                      </div>
                      <div className="w-[40%] flex-shrink-0 border-l border-gray-100 p-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Évolution</span>
                          <div className="flex items-center gap-1">
                            {[7, 14, 21].map(d => (
                              <button key={d} onClick={() => setReadyDays(d)}
                                className={`px-1.5 py-0.5 text-xs rounded transition-colors ${readyDays === d ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'}`}>
                                {d}j
                              </button>
                            ))}
                          </div>
                        </div>
                        {readyTrendLoading ? (
                          <div className="h-40 flex items-center justify-center text-xs text-gray-400">Chargement...</div>
                        ) : trendData.length > 0 ? (
                          <ResponsiveContainer width="100%" height={160}>
                            <BarChart data={trendData} margin={{ top: 4, right: 4, bottom: 40, left: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" vertical={false} />
                              <XAxis dataKey="sessionLabel" tick={{ fontSize: 9 }} interval={0} angle={-30} textAnchor="end" />
                              <YAxis tick={{ fontSize: 9 }} width={28} />
                              <Tooltip content={({ active, payload }) => {
                                if (!active || !payload?.length) return null;
                                const d = payload[0].payload;
                                return (
                                  <div className="bg-white border border-gray-200 rounded shadow p-2 text-xs">
                                    <p className="font-semibold">{d.sessionLabel}</p>
                                    {d.buildNumber && <p className="text-gray-400">Build {d.buildNumber}</p>}
                                    <p className="text-green-600">✓ {d.success}</p>
                                    <p className="text-red-500">✗ {d.failed}</p>
                                    <p className="text-gray-600">= {d.total}</p>
                                  </div>
                                );
                              }} />
                              {maxTotal > 0 && <ReferenceLine y={maxTotal} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: `${maxTotal}`, position: 'insideTopRight', fontSize: 9, fill: '#f59e0b' }} />}
                              <Bar dataKey="success" stackId="a" fill="#10b981" />
                              <Bar dataKey="failed" stackId="a" fill="#ef4444" />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="h-full flex flex-col items-center justify-center gap-1 text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                            <span className="text-2xl">📭</span>
                            <span className="text-xs font-medium">Aucune exécution sur cette période</span>
                          </div>
                        )}
                        {lastRun && <p className="text-xs text-gray-400 mt-1">Dernier run : {lastRun.sessionLabel}</p>}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </section>

            {robotEnabled && (
              <section className="space-y-6">
                <SectionHeader
                  title="Robot Framework"
                  subtitle="Résultats par pipeline pour la version sélectionnée."
                  right={
                    <div className={`text-xs px-2 py-1 rounded ${
                      robotStatus.color === 'green'
                        ? 'bg-green-100 text-green-700'
                        : robotStatus.color === 'yellow'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                    }`}>
                      {robotStatus.label}
                    </div>
                  }
                />

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-xl p-4 border border-green-200 border-l-4 border-l-green-500">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tests Passés</p>
                    <p className="text-2xl font-bold text-slate-800 mt-1">{robotStats.totalPassed}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 border border-red-200 border-l-4 border-l-red-500">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tests Échoués</p>
                    <p className="text-2xl font-bold text-slate-800 mt-1">{robotStats.totalFailed}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 border border-blue-200 border-l-4 border-l-blue-500">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Taux de Réussite</p>
                    <p className="text-2xl font-bold text-slate-800 mt-1">
                      {typeof robotStats.averageSuccessRate === 'number'
                        ? robotStats.averageSuccessRate.toFixed(1)
                        : '0.0'}%
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 border border-purple-200 border-l-4 border-l-purple-500">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Durée Totale</p>
                    <p className="text-2xl font-bold text-slate-800 mt-1">{formatDuration(robotStats.totalDuration)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  {robotDomainGroups.map((group) => {
                    const pipelineStatus = getStatusFromSuccessRate(Number(group.successRate) || 0);
                    const trendArrays = group.pipelineIds.map(id => robotTrendData[id] || []);
                    const trendByLabel = {};
                    trendArrays.forEach(arr => arr.forEach(e => {
                      const k = e.sessionLabel;
                      if (!trendByLabel[k]) trendByLabel[k] = { ...e, success: 0, failed: 0, total: 0 };
                      trendByLabel[k].success += e.success || 0;
                      trendByLabel[k].failed += e.failed || 0;
                      trendByLabel[k].total += e.total || 0;
                    }));
                    const trendData = Object.values(trendByLabel).sort((a, b) => (a.sessionLabel || '').localeCompare(b.sessionLabel || ''));
                    const lastRun = trendData[trendData.length - 1];
                    const maxTotal = trendData.length > 0 ? Math.max(...trendData.map(d => d.total)) : 0;
                    return (
                    <div key={group.domain} className={`rounded-xl shadow-md overflow-hidden ${group.isRunning ? 'bg-blue-50 border border-blue-200' : 'bg-white'}`}>
                      <div className="flex">
                        <div className="flex-1 p-6">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-semibold text-gray-900">{group.domain}</h3>
                              {group.env && (
                                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                                  {group.env}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {group.isRunning && (
                                <a
                                  href={group.runningBuildUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300 hover:bg-blue-200 transition-colors"
                                  title="Voir la pipeline en cours sur Azure DevOps"
                                >
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  En cours
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${
                                pipelineStatus.color === 'green'
                                  ? 'bg-green-100 text-green-800 border-green-300'
                                  : pipelineStatus.color === 'yellow'
                                    ? 'bg-yellow-100 text-yellow-800 border-yellow-300'
                                    : 'bg-red-100 text-red-800 border-red-300'
                              }`}>
                                {pipelineStatus.color === 'green' ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                                {pipelineStatus.label}
                              </span>
                            </div>
                          </div>
                          <div className="space-y-3">
                            {group.executionDate && (
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Date d'exécution:</span>
                                <span className="font-medium">
                                  <a
                                    href={group.executionDateBuildUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
                                    title="Voir le build sur Azure DevOps"
                                  >
                                    {formatDate(group.executionDate)}
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Tests:</span>
                              <span className="font-medium">
                                <span className="text-green-600">{group.passedTests}</span> /
                                <span className="text-red-600">{group.failedTests}</span> /
                                {group.skippedTests > 0 && <><span className="text-gray-500">{group.skippedTests}</span> / </>}
                                <span className="text-gray-900">{group.totalTests}</span>
                              </span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Durée:</span>
                              <span className="font-medium">{formatDuration(group.totalDuration)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Taux de réussite:</span>
                              <span className="font-medium">{group.successRate}%</span>
                            </div>
                            <div className="w-full bg-gray-100 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full transition-all ${
                                  Number(group.successRate) >= 90 ? 'bg-green-500' :
                                  Number(group.successRate) >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${Math.min(Number(group.successRate) || 0, 100)}%` }}
                              ></div>
                            </div>
                            <div className="flex gap-2 mt-4 flex-wrap">
                              {group.pipelineIds.map(pid => {
                                const pname = (ROBOT_PIPELINE_CONFIGS[selectedVersion]?.pipelinesList || []).find(p => p.id === pid)?.name || `Pipeline ${pid}`;
                                return (
                                  <div key={pid} className="flex-1 min-w-[140px] flex gap-1.5">
                                    <button
                                      onClick={() => handleTriggerPipeline(pid)}
                                      disabled={triggerLoading.has(pid)}
                                      className="bg-gray-100 text-gray-700 px-2 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-1 text-sm border border-gray-200 disabled:opacity-50"
                                      title="Lancer le pipeline"
                                    >
                                      {triggerLoading.has(pid) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                                    </button>
                                    <Link
                                      to={`/robotframework/pipeline/${pid}?returnTo=${returnToParam}`}
                                      className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-1.5 text-sm font-medium"
                                    >
                                      <Eye className="w-3.5 h-3.5" />
                                      {pname}
                                    </Link>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                        <div className="w-[40%] flex-shrink-0 border-l border-gray-100 p-4 bg-gray-50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Évolution</span>
                            <div className="flex items-center gap-1">
                              {[7, 14, 21].map(d => (
                                <button key={d} onClick={() => setRobotDays(d)}
                                  className={`px-1.5 py-0.5 text-xs rounded transition-colors ${robotDays === d ? 'bg-blue-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'}`}>
                                  {d}j
                                </button>
                              ))}
                            </div>
                          </div>
                          {robotTrendLoading ? (
                            <div className="h-40 flex items-center justify-center text-xs text-gray-400">Chargement...</div>
                          ) : trendData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={160}>
                              <BarChart data={trendData} margin={{ top: 4, right: 4, bottom: 40, left: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="sessionLabel" tick={{ fontSize: 9 }} interval={0} angle={-30} textAnchor="end" />
                                <YAxis tick={{ fontSize: 9 }} width={28} />
                                <Tooltip content={({ active, payload }) => {
                                  if (!active || !payload?.length) return null;
                                  const d = payload[0].payload;
                                  return (
                                    <div className="bg-white border border-gray-200 rounded shadow p-2 text-xs">
                                      <p className="font-semibold">{d.sessionLabel}</p>
                                      <p className="text-green-600">✓ {d.success}</p>
                                      <p className="text-red-500">✗ {d.failed}</p>
                                      <p className="text-gray-600">= {d.total}</p>
                                    </div>
                                  );
                                }} />
                                {maxTotal > 0 && <ReferenceLine y={maxTotal} stroke="#f59e0b" strokeDasharray="4 4" label={{ value: `${maxTotal}`, position: 'insideTopRight', fontSize: 9, fill: '#f59e0b' }} />}
                                <Bar dataKey="success" stackId="a" fill="#10b981" />
                                <Bar dataKey="failed" stackId="a" fill="#ef4444" />
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="h-full flex flex-col items-center justify-center gap-1 text-gray-400 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                              <span className="text-2xl">📭</span>
                              <span className="text-xs font-medium">Aucune exécution sur cette période</span>
                            </div>
                          )}
                          {lastRun && <p className="text-xs text-gray-400 mt-1">Dernier run : {lastRun.sessionLabel}</p>}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </section>
            )}
              </>
            )}

            {(selectedVersion === 'develop' || selectedVersion === '26.2') && (
              <section className="space-y-4">
                <SectionHeader
                  title="Bugs TA"
                  subtitle="New TA Regression — toutes les squads"
                  right={
                    <span className={`text-sm font-semibold px-3 py-1 rounded-full ${
                      allBugs.some(b => b.severity === '1 - Critical')
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {allBugs.length} bug{allBugs.length !== 1 ? 's' : ''}
                    </span>
                  }
                />

                {/* Résumé par sévérité */}
                <div className="grid grid-cols-4 gap-3">
                  {Object.entries(SEV_CONFIG).map(([key, cfg]) => {
                    const count = allBugs.filter(b => b.severity === key).length;
                    return (
                      <div key={key} className={`bg-white rounded-xl border ${count > 0 ? 'border-gray-200 shadow-sm' : 'border-gray-100 opacity-50'} p-4 flex flex-col items-center gap-1`}>
                        <span className={`text-2xl font-bold ${count > 0 ? 'text-gray-900' : 'text-gray-400'}`}>{count}</span>
                        <SevPill severity={key} />
                      </div>
                    );
                  })}
                </div>

                {/* Aperçu des 3 premiers bugs */}
                {allBugs.length > 0 && (
                  <div className="space-y-2">
                    {allBugs.slice(0, 3).map(b => <BugCard key={b.id} bug={b} />)}
                  </div>
                )}

                <button
                  onClick={() => setShowBugsModal(true)}
                  className="w-full bg-slate-800 text-white px-4 py-2.5 rounded-xl hover:bg-slate-700 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                >
                  <Eye className="w-4 h-4" />
                  Voir tous les bugs ({allBugs.length})
                </button>
              </section>
            )}

            <section className="space-y-4">
              <SectionHeader
                title="Root Cause des échecs"
                subtitle="Classification sur les 90 derniers jours"
              />
              {(() => {
                const totalKO = squadSuiteData.reduce((s, sq) => s + (sq.failedTests || 0), 0);
                if (!totalKO || (selectedVersion !== 'develop' && selectedVersion !== '26.2')) return null;
                const totalAnalyzed = rootCauseKpi ? Object.values(rootCauseKpi.totals).reduce((a, b) => a + b, 0) : 0;
                return (
                  <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                    <p className="text-sm text-amber-800">
                      <span className="font-bold">{totalKO} tests KO</span> dans les builds actuels
                      {totalAnalyzed > 0 && <span className="text-amber-600"> · {totalAnalyzed} analysés sur les 90 derniers jours</span>}
                    </p>
                  </div>
                );
              })()}
              {rootCauseKpi ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                    <p className="text-sm font-semibold text-gray-700 mb-3">Répartition totale</p>
                    {(() => {
                      const total = Object.values(rootCauseKpi.totals).reduce((a, b) => a + b, 0);
                      return (
                        <div className="space-y-3">
                          {[
                            { key: 'Bug', color: 'bg-red-500', text: 'text-red-700' },
                            { key: 'Test', color: 'bg-amber-500', text: 'text-amber-700' },
                            { key: 'Environment', label: 'Env', color: 'bg-blue-500', text: 'text-blue-700' },
                          ].map(({ key, label, color, text }) => {
                            const count = rootCauseKpi.totals[key] || 0;
                            const pct = total > 0 ? Math.round(count / total * 100) : 0;
                            return (
                              <div key={key}>
                                <div className="flex justify-between text-xs mb-1">
                                  <span className={`font-medium ${text}`}>{label || key}</span>
                                  <span className="text-gray-500">{count} ({pct}%)</span>
                                </div>
                                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                  <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            );
                          })}
                          <p className="text-xs text-gray-400 mt-2">{total} entrée{total !== 1 ? 's' : ''} au total</p>
                        </div>
                      );
                    })()}
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                    <p className="text-sm font-semibold text-gray-700 mb-3">Évolution quotidienne <span className="font-normal text-gray-400">(% par catégorie)</span></p>
                    {rootCauseKpi.trend?.length > 0 ? (
                      <>
                        <ResponsiveContainer width="100%" height={160}>
                          <BarChart data={rootCauseKpi.trend} stackOffset="expand" margin={{ top: 4, right: 8, bottom: 0, left: -10 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={val => { if (!val || typeof val !== 'string') return val; const p = val.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}` : val; }} />
                            <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${Math.round(v * 100)}%`} domain={[0, 1]} />
                            <Tooltip
                              formatter={(value, name, { payload }) => {
                                const total = (payload?.Bug || 0) + (payload?.Test || 0) + (payload?.Environment || 0);
                                const pct = total > 0 ? Math.round(value / total * 100) : 0;
                                return [`${value} test${value !== 1 ? 's' : ''} (${pct}%)`, name === 'Environment' ? 'Env' : name];
                              }}
                              labelFormatter={label => { const p = (label || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}` : label; }}
                            />
                            <Bar dataKey="Bug" stackId="rc" fill="#ef4444" />
                            <Bar dataKey="Test" stackId="rc" fill="#f59e0b" />
                            <Bar dataKey="Environment" stackId="rc" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                        <p className="text-xs text-gray-400 text-center mt-1">
                          {rootCauseKpi.trend.reduce((s, d) => s + (d.Bug || 0) + (d.Test || 0) + (d.Environment || 0), 0)} tests analysés au total
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-gray-400 text-center py-8">Pas encore de données</p>
                    )}
                  </div>
                  {Object.keys(rootCauseKpi.byVersion || {}).length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                      <p className="text-sm font-semibold text-gray-700 mb-3">Par version</p>
                      <div className="space-y-3">
                        {Object.entries(rootCauseKpi.byVersion).sort(([a], [b]) => b.localeCompare(a)).map(([ver, counts]) => {
                          const total = (counts.Bug || 0) + (counts.Test || 0) + (counts.Environment || 0);
                          if (!total) return null;
                          return (
                            <div key={ver}>
                              <div className="flex justify-between text-xs mb-1">
                                <span className="font-medium text-gray-700">{ver}</span>
                                <span className="text-gray-400">{total} analysé{total !== 1 ? 's' : ''}</span>
                              </div>
                              <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex">
                                {counts.Bug > 0 && <div className="h-full bg-red-500" style={{ width: `${counts.Bug / total * 100}%` }} />}
                                {counts.Test > 0 && <div className="h-full bg-amber-500" style={{ width: `${counts.Test / total * 100}%` }} />}
                                {counts.Environment > 0 && <div className="h-full bg-blue-500" style={{ width: `${counts.Environment / total * 100}%` }} />}
                              </div>
                              <div className="flex gap-3 mt-0.5 text-xs">
                                {counts.Bug > 0 && <span className="text-red-600">{counts.Bug} Bug</span>}
                                {counts.Test > 0 && <span className="text-amber-600">{counts.Test} Test</span>}
                                {counts.Environment > 0 && <span className="text-blue-600">{counts.Environment} Env</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {Object.keys(rootCauseKpi.bySquad || {}).length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
                      <p className="text-sm font-semibold text-gray-700 mb-3">Par équipe</p>
                      <div className="space-y-3">
                        {Object.entries(rootCauseKpi.bySquad).map(([suite, counts]) => {
                          const total = (counts.Bug || 0) + (counts.Test || 0) + (counts.Environment || 0);
                          if (!total) return null;
                          return (
                            <div key={suite}>
                              <div className="flex justify-between text-xs mb-1">
                                <span className="font-medium text-gray-700 truncate max-w-[65%]">{suite}</span>
                                <span className="text-gray-400 shrink-0">{total} analysé{total !== 1 ? 's' : ''}</span>
                              </div>
                              <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex">
                                {counts.Bug > 0 && <div className="h-full bg-red-500" style={{ width: `${counts.Bug / total * 100}%` }} />}
                                {counts.Test > 0 && <div className="h-full bg-amber-500" style={{ width: `${counts.Test / total * 100}%` }} />}
                                {counts.Environment > 0 && <div className="h-full bg-blue-500" style={{ width: `${counts.Environment / total * 100}%` }} />}
                              </div>
                              <div className="flex gap-3 mt-0.5 text-xs">
                                {counts.Bug > 0 && <span className="text-red-600">{counts.Bug} Bug</span>}
                                {counts.Test > 0 && <span className="text-amber-600">{counts.Test} Test</span>}
                                {counts.Environment > 0 && <span className="text-blue-600">{counts.Environment} Env</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-100 p-6 text-center">
                  <p className="text-sm text-gray-400">Chargement...</p>
                </div>
              )}
            </section>
          </>
        )}
      </main>
      <BugsModalWrapper showBugsModal={showBugsModal} setShowBugsModal={setShowBugsModal} allBugs={allBugs} />
    </div>
  );
};

// Modal liste des bugs
const BugsModal = ({ isOpen, onClose, allBugs }) => {
  const [sevFilter, setSevFilter] = React.useState('all');
  if (!isOpen) return null;

  const filtered = sevFilter === 'all' ? allBugs : allBugs.filter(b => b.severity === sevFilter);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-slate-800 to-slate-700 text-white rounded-t-2xl">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-bold">Bugs TA</h2>
              <p className="text-slate-300 text-sm mt-0.5">New TA Regression — toutes les squads</p>
              <a
                href="https://dev.azure.com/chapsvision/Fabrique/_queries/query/67020172-e295-4bb7-981f-87758ab02f7d/"
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-blue-300 hover:text-blue-200 mt-2 transition-colors"
              >
                Voir la query AzDO <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1">
              <XCircle className="w-5 h-5" />
            </button>
          </div>

          {/* Compteurs sévérité */}
          <div className="grid grid-cols-4 gap-2 mt-4">
            {Object.entries(SEV_CONFIG).map(([key, cfg]) => {
              const count = allBugs.filter(b => b.severity === key).length;
              const active = sevFilter === key;
              return (
                <button
                  key={key}
                  onClick={() => setSevFilter(active ? 'all' : key)}
                  className={`rounded-xl p-2.5 text-center transition-all border ${
                    active
                      ? 'bg-white/20 border-white/40 scale-105'
                      : 'bg-white/5 border-white/10 hover:bg-white/10'
                  }`}
                >
                  <div className="text-xl font-bold text-white">{count}</div>
                  <div className="text-xs text-slate-300 mt-0.5">{cfg.label}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Filtres actifs */}
        {sevFilter !== 'all' && (
          <div className="px-6 pt-3 flex items-center gap-2">
            <span className="text-xs text-gray-500">Filtre actif :</span>
            <SevPill severity={sevFilter} />
            <button onClick={() => setSevFilter('all')} className="text-xs text-gray-400 hover:text-gray-600 underline">
              effacer
            </button>
          </div>
        )}

        {/* Liste */}
        <div className="p-6 overflow-y-auto flex-1 space-y-2">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <CheckCircle className="w-10 h-10 mx-auto mb-3 text-green-400" />
              <p className="font-medium">Aucun bug pour cette sévérité</p>
            </div>
          ) : (
            filtered.map(b => <BugCard key={b.id} bug={b} />)
          )}
        </div>

        <div className="flex items-center justify-between p-4 border-t bg-gray-50 rounded-b-2xl">
          <span className="text-sm text-gray-500">
            {filtered.length} bug{filtered.length !== 1 ? 's' : ''} affiché{filtered.length !== 1 ? 's' : ''}
          </span>
          <button onClick={onClose} className="bg-slate-800 text-white px-5 py-2 rounded-lg hover:bg-slate-700 transition-colors text-sm font-medium">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

const BugsModalWrapper = ({ showBugsModal, setShowBugsModal, allBugs }) => (
  <BugsModal isOpen={showBugsModal} onClose={() => setShowBugsModal(false)} allBugs={allBugs} />
);

export default TestsOverviewPage;
